import { capEditLocationApi, execute } from '@/utils/api';
import type { EntityLocation, EntitySecondaryOrganizationMember } from '@/utils/api/api';
import type {
  EntityAdministrationalLocation,
  EntityAdministrationalLocationForCreate,
  EntityAdministrationalLocationForUpdate,
  EntityAdministrationalSecondaryMember,
  EntityAdministrationalSecondaryMemberForLocation,
  EntitySuccessMsg,
  GetCollaborators200Response,
  GetLocations200Response,
  GetLocationsRequest,
  GetSecondaryOrganizationMembers200Response
} from '@/utils/api/maintenance/api';
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useDefaults } from 'vuetify';

/**
 * 対象先関連のstore
 */
export const useLocationStore = defineStore(
  'location',
  () => {
    /** ----------------------------------------------------------------------
     * 対象先関連
     ---------------------------------------------------------------------- */
    /**
     * 検索条件（サービス）
     * ※ 指定なし: -1
     */
    const condService = ref<number>(-1);

    /** 検索条件（GC番号） */
    const condGcNo = ref<string | undefined>(undefined);

    /** 検索条件（号機番号） */
    const condGouki = ref<string | undefined>(undefined);

    /** 検索条件（電計番号） */
    const condDenkei = ref<string | undefined>(undefined);

    /** 検索条件（対象先名称） */
    const condLocationName = ref<string | undefined>(undefined);

    /** 対象先 - リスト */
    const locations = ref<Array<EntityAdministrationalLocation>>([]);

    /** 対象先 - 総件数 */
    const locationsTotal = ref<number>(0);

    /** 対象先 - ページネーション */
    const locationItemsPerPage = ref<number>(25);
    const locationPage = ref<number>(1);

    /** 対象先（詳細画面表示用） */
    const selectedLocation = ref<EntityAdministrationalLocation | undefined>(undefined);

    /**
     * 対象先のリストを取得する
     * @param condition 検索条件
     * @param pageMax 1ページの最大件数
     * @param pageNum 指定ページ数
     * @returns
     */
    const fetchLocations = (
      pageMax: number,
      pageNum: number,
      serviceId?: number,
      gcNo?: string,
      gouki?: string,
      denkei?: string,
      name?: string
    ): Promise<any> => {
      const request: GetLocationsRequest = {
        serviceId: serviceId,
        gcNo: gcNo?.length ? gcNo : undefined,
        gouki: gouki?.length ? gouki : undefined,
        denkei: denkei?.length ? denkei : undefined,
        name: name?.length ? name : undefined,
        pageMax: pageMax,
        pageNum: pageNum
      };
      return new Promise((resolve, reject) => {
        execute(() => capEditLocationApi.getLocations(request), true)
          .then((data: GetLocations200Response) => {
            console.info('対象先 一覧取得成功');
            locations.value = data.items ?? [];
            locationsTotal.value = data.total ?? 0;
            resolve(data);
          })
          .catch((e: unknown) => {
            console.warn('対象先 一覧取得失敗');
            locations.value = [];
            locationsTotal.value = 0;
            reject();
          });
      });
    };

    /**
     * 対象先を新規登録する
     * @param id
     * @param locationForCreate
     * @returns
     */
    const createLocation = (
      locationForCreateCreate: EntityAdministrationalLocationForCreate
    ): Promise<any> => {
      return new Promise((resolve, reject) => {
        execute(() => capEditLocationApi.createLocation(locationForCreateCreate), true)
          .then((data: EntityAdministrationalLocation) => {
            console.info('対象先 新規登録成功');
            resolve(data);
          })
          .catch((e: unknown) => {
            console.warn('対象先 新規登録失敗');
            reject();
          });
      });
    };

    /**
     * 対象先を更新する
     * @param id
     * @param locationForUpdate
     * @returns
     */
    const updateLocation = (
      id: number,
      locationForUpdate: EntityAdministrationalLocationForUpdate
    ): Promise<any> => {
      return new Promise((resolve, reject) => {
        execute(() => capEditLocationApi.updateLocation(id, locationForUpdate), true)
          .then((data: EntityAdministrationalLocation) => {
            console.info('対象先 更新成功');
            selectedLocation.value = data;
            resolve(data);
          })
          .catch((e: unknown) => {
            console.warn('対象先 更新失敗');
            reject();
          });
      });
    };

    /**
     * 対象先を削除する
     * @param id ユーザーID
     * @returns
     */
    const deleteLocation = (id: number): Promise<void> => {
      return new Promise((resolve, reject) => {
        execute(() => capEditLocationApi.deleteLocation(id), true)
          .then((data: EntitySuccessMsg[]) => {
            console.info('対象先 削除成功');
            // リストから削除する
            const index = locations.value.findIndex((v) => v.locationId === id);
            if (index != -1) {
              locations.value.splice(index, 1);
            }
            resolve();
          })
          .catch((e: unknown) => {
            console.warn('対象先 削除失敗');
            reject();
          });
      });
    };

    /** ----------------------------------------------------------------------
     * 担当者（二次専門組織）関連
     ---------------------------------------------------------------------- */
    /**
     * 検索条件（サービス）
     * ※ 指定なし: -1
     */
    const condAssignedAssignedService = ref<number>(-1);

    /** 検索条件（組織名称） */
    const condOrganizationName = ref<string | undefined>(undefined);

    /** 検索条件（ユーザー名） */
    const condMemberName = ref<string | undefined>(undefined);

    /** 検索条件（Email（ログインID）） */
    const condEmail = ref<string | undefined>(undefined);

    /** 担当者 - リスト */
    const collaborators = ref<Array<EntityAdministrationalSecondaryMemberForLocation>>([]);

    /** 担当者 - 総件数 */
    const collaboratorsTotal = ref<number>(0);

    /** 担当者 - ページネーション */
    const collaboratorItemsPerPage = ref<number>(25);
    const collaboratorPage = ref<number>(1);

    /**
     * 担当者のリストを取得する
     * @param pageMax 1ページの最大件数
     * @param pageNum 指定ページ数
     * @returns
     */
    const fetchCollaborators = (
      pageMax: number,
      pageNum: number,
      locationId: number,
      userName?: string,
      organizationName?: string,
      email?: string,
      serviceId?: number
    ): Promise<any> => {
      return execute(
        () =>
          capEditLocationApi.getCollaborators(
            locationId,
            pageMax,
            pageNum,
            userName?.length ? userName : undefined,
            organizationName?.length ? organizationName : undefined,
            email?.length ? email : undefined,
            serviceId
          ),
        true
      )
        .then((data: GetCollaborators200Response) => {
          console.info('担当者 一覧取得成功');
          // 担当者リストを更新
          collaborators.value = data.items ?? [];
          // 総件数を更新
          collaboratorsTotal.value = data.total ?? 0;
        })
        .catch((e: unknown) => {
          console.warn('担当者 一覧取得失敗');
          collaborators.value = [];
          collaboratorsTotal.value = 0;
        });
    };

    /**
     * 担当者の選択を解除する
     * @param locationId
     * @param memberId
     * @returns
     */
    const removeCollaborator = (locationId: number, memberId: number): Promise<void> => {
      return new Promise((resolve, reject) => {
        execute(() => capEditLocationApi.deleteCollaborator(locationId, memberId), true)
          .then((data: EntitySuccessMsg[]) => {
            console.info('担当者 選択解除成功');
            // リストから削除する
            const index = collaborators.value.findIndex((v) => v.memberId === memberId);
            if (index != -1) {
              collaborators.value.splice(index, 1);
            }
            resolve();
          })
          .catch((e: unknown) => {
            console.warn('担当者 選択解除失敗');
            reject();
          });
      });
    };

    /**
     * storeの初期化
     */
    const clear = (): void => {
      condService.value = -1;
      condGcNo.value = undefined;
      condGouki.value = undefined;
      condDenkei.value = undefined;
      condLocationName.value = undefined;
      locations.value = [];
      locationsTotal.value = 0;
      locationItemsPerPage.value = 25;
      locationPage.value = 0;
      selectedLocation.value = undefined;

      condAssignedAssignedService.value = -1;
      condOrganizationName.value = undefined;
      condMemberName.value = undefined;
      condEmail.value = undefined;
      collaborators.value = [];
      collaboratorsTotal.value = 0;
      collaboratorItemsPerPage.value = 25;
      collaboratorPage.value = 0;
    };

    return {
      condService,
      condGcNo,
      condGouki,
      condDenkei,
      condLocationName,
      locations,
      locationsTotal,
      locationItemsPerPage,
      locationPage,
      selectedLocation,
      fetchLocations,
      createLocation,
      updateLocation,
      deleteLocation,
      condAssignedAssignedService,
      condOrganizationName,
      condMemberName,
      condEmail,
      collaborators,
      collaboratorsTotal,
      collaboratorItemsPerPage,
      collaboratorPage,
      fetchCollaborators,
      removeCollaborator,
      clear
    };
  },
  { persist: { storage: sessionStorage } }
);
